import { uuidv7 } from "uuidv7"

import { GlobalComposable } from "~/classes/composable/GlobalComposable"
import { PropertySchema } from "~/classes/entity"
import { InterfaceSelection } from "~/classes/entity/InterfaceSelection"
import { InterfaceFile } from "~/classes/file/InterfaceFile"
import { InterfaceSidebarState } from "~/classes/generated/interface"
import { sendFilePropertiesRequests } from "~/classes/handlers/file/properties"
import { sendSelfSidebarRequest } from "~/classes/handlers/user/sidebar"
import { sendSelfUpdateRequests } from "~/classes/handlers/user/update"
import { InterfaceSearch } from "~/classes/search/InterfaceSearch"
import { InterfaceUser } from "~/classes/users/InterfaceUser"
import { defaultUserInterfaceSettings } from "~/utils/flatbuffers/user"

class Sidebar extends GlobalComposable<{
  entries: Record<string, UserSidebarItem>
  /** Record of file-id/search-id to User Sidebar Item Id */
  pending: Record<string, UserSidebarItemId>
  width: number
  state: InterfaceSidebarState
  userIdForEvents?: string
}> {
  override defaultState = () => ({
    entries: {},
    pending: {},
    width: defaultUserInterfaceSettings().sidebarWidth,
    state: InterfaceSidebarState.Favorites,
  })

  override onCreate = () => {
    const { userInterfaceSettings } = useSelf()
    const { addInterfaceEventListener } = useEvents()

    addInterfaceEventListener("selfSidebarResponse", this.onSelfSidebarResponse)
    watchImmediate(userInterfaceSettings, (settings) => {
      this.state.value.state = settings.sidebarState
      this.state.value.width = settings.sidebarWidth
    })
  }

  override onDestroy = () => {
    const { removeInterfaceEventListener } = useEvents()
    removeInterfaceEventListener("selfSidebarResponse", this.onSelfSidebarResponse)
  }

  clearSidebarEntriesAndEventHandlers = () => {
    const { removeEntityEventListener } = useEvents()
    for (const entry of Object.values(this.state.value.entries)) {
      if (!entry.fileItem)
        continue // TODO: sidebar searches
      removeEntityEventListener("fileProperties", entry.fileItem.fileId, this.onSidebarFileProperties)
      removeEntityEventListener("fileDeleted", entry.fileItem.fileId, this.onSidebarFileDeleted)
    }
    this.state.value.entries = {}
  }

  /** Sidebar files organized by file id */
  sidebarFiles = computed(() => {
    const files = Object
      .values(this.state.value.entries)
      .filter(e => !!e.fileItem)
      .map(({ fractionalIndex, fileItem }) => {
        return { fractionalIndex, file: new InterfaceFile(fileItem!) } as UserSidebarInterfaceFile
      })
    return dictByKey(files, f => f.file.fileId)
  })

  /** Sidebar files sorted by fractional index */
  sortedSidebarFiles = computed(() => {
    return Object
      .values(this.state.value.entries)
      .filter(e => !!e.fileItem)
      .map(({ fractionalIndex, fileItem }) => {
        return { fractionalIndex, file: new InterfaceFile(fileItem!) } as UserSidebarInterfaceFile
      })
      .sort((a, b) => a.fractionalIndex.localeCompare(b.fractionalIndex))
  })

  sidebarSearches = computed(() => {
    const searches = Object
      .values(this.state.value.entries)
      .filter(e => !!e.searchItem)
      .map(({ fractionalIndex, searchItem }) => {
        return { fractionalIndex, search: new InterfaceSearch(searchItem!) } as UserSidebarInterfaceSearch
      })
    return dictByKey(searches, s => s.search.searchId())
  })

  sortedSidebarSearches: ComputedRef<UserSidebarInterfaceSearch[]> = computed(() => {
    return Object
      .values(this.state.value.entries)
      .filter(e => !!e.searchItem)
      .map(({ fractionalIndex, searchItem }) => {
        return { fractionalIndex, search: new InterfaceSearch(searchItem!) } as UserSidebarInterfaceSearch
      })
      .sort((a, b) => a.fractionalIndex.localeCompare(b.fractionalIndex))
  })

  /**
   * Adds a set of files to the sidebar by constructing fractional indexes for the files
   * between the boundaries specified.
   *
   * TODO: randomize the key generation somewhat to prevent two users from generating identical keys
   */
  addFilesToSidebar = async (
    files: Record<string, InterfaceFile>,
    [low, high]: [string | undefined, string | undefined] = [undefined, undefined],
  ) => {
    const { state: myself } = useSelf()
    const userId = myself.value.user?.userId
    if (!isDefined(userId))
      throw new Error("Only users can add to sidebar")

    const timestamp = uuidv7()
    const indices = generateIndicesBetween(len(files), low, high)
    const properties: Record<string, UserProperty> = {}

    // remove any files that were there before (should we allow duplicates?)
    const fileIds = new Set(Object.values(files).map(f => f.fileId))
    for (const { fractionalIndex, fileItem } of Object.values(this.state.value.entries)) {
      if (!fileItem?.fileId || !fileIds.has(fileItem.fileId))
        continue
      const property = await InterfaceUser.createPropertyForSidebarFile({ userId, fileId: fileItem.fileId, fractionalIndex, timestamp })
      property.deleted = true
      properties[property.propertyId] = property
    }

    for (const [file, fractionalIndex] of zip(Object.values(files), indices)) {
      const property = await InterfaceUser.createPropertyForSidebarFile({ userId, fileId: file.fileId, fractionalIndex, timestamp })
      properties[property.propertyId] = property
    }

    const updates: SelfUpdateRequestDatagram[] = Object
      .values(properties)
      .map((property) => {
        const user = InterfaceUser.blank(userId)
        user.addProperty(property)
        return {
          user: user.inner(),
          requestId: generateWorkerRequestId(),
          offline: false,
        }
      })

    sendSelfUpdateRequests(updates)
  }

  removeFilesFromSidebar = async (
    files: Record<string, InterfaceFile>,
  ) => {
    const { state: myself } = useSelf()
    const userId = myself.value.user?.userId
    if (!isDefined(userId))
      throw new Error("Only users can add to sidebar")

    const timestamp = uuidv7()
    const properties: Record<string, UserProperty> = {}

    // remove any files that were there before (should we allow duplicates?)
    const fileIds = new Set(Object.values(files).map(f => f.fileId))
    for (const { fractionalIndex, fileItem } of Object.values(this.state.value.entries)) {
      if (!fileItem?.fileId || !fileIds.has(fileItem.fileId))
        continue
      const property = await InterfaceUser.createPropertyForSidebarFile({ userId, fileId: fileItem.fileId, fractionalIndex, timestamp })
      property.deleted = true
      properties[property.propertyId] = property
    }

    const updates: SelfUpdateRequestDatagram[] = Object
      .values(properties)
      .map((property) => {
        const user = InterfaceUser.blank(userId)
        user.addProperty(property)
        return {
          user: user.inner(),
          requestId: generateWorkerRequestId(),
          offline: false,
        }
      })
    if (len(updates) > 0)
      sendSelfUpdateRequests(updates)
  }

  showSidebar = () => {
    this.state.value.state = InterfaceSidebarState.Favorites
  }

  hideSidebar = () => {
    this.state.value.state = InterfaceSidebarState.Hidden
  }

  persistSidebar = () => {
    const { isAuthenticated, persistUserInterfaceSettings } = useSelf()

    if (isAuthenticated.value) {
      persistUserInterfaceSettings({ sidebarState: this.state.value.state })
    }
  }

  showSidebarAndPersist = () => {
    this.showSidebar()
    this.persistSidebar()
  }

  hideSidebarAndPersist = () => {
    this.hideSidebar()
    this.persistSidebar()
  }

  requestSelfSidebar = () => {
    const request: SelfSidebarRequestDatagram = {
      requestId: generateWorkerRequestId(),
      offline: false,
    }
    sendSelfSidebarRequest(request)
    return request
  }

  onSelfSidebarResponse = (response: SelfSidebarResponseDatagram) => {
    const { cacheState } = useCache()
    const { addEntityEventListener, removeEntityEventListener } = useEvents()

    if (response.error || !response.sidebarItems)
      throw new Error("Requesting sidebar items failed")

    this.clearSidebarEntriesAndEventHandlers()
    for (const entry of Object.values(response.sidebarItems)) {
      const file = InterfaceFile.from(entry.fileItem)
      if (!file) {
        console.warn("Saved searches not supported yet")
        continue
      }
      this.state.value.entries[entry.fractionalIndex] = entry
      addEntityEventListener("fileProperties", file.fileId, this.onSidebarFileProperties)
      addEntityEventListener("fileDeleted", file.fileId, this.onSidebarFileDeleted)
    }

    if (!cacheState.value.userId)
      throw new Error("No user id in cache state")

    // if we received a new sidebar response from the server, then we know we are
    // ready to receive new user info updates as we have a fresh sidebar.
    // TODO: write a handler that requests an updated sidebar on reconnection,
    //       or perhaps write that into the useSelf handler?
    if (!response.offline && cacheState.value.userId !== this.state.value.userIdForEvents) {
      if (isDefined(this.state.value.userIdForEvents))
        removeEntityEventListener("userProperties", cacheState.value.userId, this.onUserProperties)
      addEntityEventListener("userProperties", cacheState.value.userId, this.onUserProperties)
      this.state.value.userIdForEvents = cacheState.value.userId
    }
  }

  onUserProperties = (event: UserEvent) => {
    const { addEntityEventListener } = useEvents()

    const iUser = InterfaceUser.fromEvent(event)
    const properties = iUser.sidebarEntryProperties()
    for (const property of properties) {
      if (property.value.type !== PropertySchema.UserSidebarItemIdValue)
        throw new Error("Not a sidebar property")
      const entry = property.value.UserSidebarItemId
      if (!entry.fileItemId)
        continue // TODO: search items
      if (property.deleted)
        return this.onSidebarFileRemoved(entry.fileItemId)
      if (this.state.value.entries[entry.fractionalIndex])
        continue // File already exists in sidebar

      // Now we need to add the file and retrieve its properties
      this.state.value.pending[entry.fileItemId] = entry
      addEntityEventListener("fileProperties", entry.fileItemId, this.onSidebarFileProperties)
      addEntityEventListener("fileDeleted", entry.fileItemId, this.onSidebarFileDeleted)
      sendFilePropertiesRequests([{
        requestId: generateWorkerRequestId(),
        fileId: entry.fileItemId,
        offline: false,
        selection: InterfaceSelection.minimumInterfaceSelection().inner(),
        pathSelection: InterfaceSelection.minimumPathSelection().inner(),
      }])
    }
  }

  onSidebarFileProperties = (event: FileEvent) => {
  // TODO: what if you no longer have access to this file?

    const iFile = InterfaceFile.fromEvent(event)
    let fractionalIndex: string | undefined

    // If the file was pending, then we check that list first
    if (this.state.value.pending[iFile.fileId]) {
      fractionalIndex = this.state.value.pending[iFile.fileId].fractionalIndex
      delete this.state.value.pending[iFile.fileId]
    }
    else {
      fractionalIndex = this.sidebarFiles.value[iFile.fileId]?.fractionalIndex
    }

    if (!isDefined(fractionalIndex))
      return console.error("Received file properties for nonexistent sidebar file", event)

    const existing = InterfaceFile.from(this.state.value.entries[fractionalIndex]?.fileItem)
    if (existing) {
      const sidebarFile = new InterfaceFile(this.state.value.entries[fractionalIndex]!.fileItem!).mergeWithNewer(iFile)
      this.state.value.entries[fractionalIndex]!.fileItem = sidebarFile.inner()
    }
    else {
      this.state.value.entries[fractionalIndex] = { fractionalIndex, fileItem: iFile.inner() }
    }
  }

  onSidebarFileDeleted = (event: FileEvent) => {
    const iFile = InterfaceFile.fromEvent(event)
    this.onSidebarFileRemoved(iFile.fileId)
  }

  onSidebarFileRemoved = (fileId: string) => {
    const { removeEntityEventListener } = useEvents()

    const entry = this.sidebarFiles.value[fileId]
    if (!entry)
      return console.error("Can't remove sidebar file, already removed")

    delete this.state.value.entries[entry.fractionalIndex]
    removeEntityEventListener("fileProperties", fileId, this.onSidebarFileProperties)
    removeEntityEventListener("fileDeleted", fileId, this.onSidebarFileDeleted)
  }
}

const sidebar = new Sidebar("sidebar", theme.colors.blue.hex)
export default function () {
  return sidebar.use()
}
